// Jellis Craig detail-page parser. The site (Craft CMS, listings fed from
// ListOnce) renders the listing as server-side HTML; its JSON-LD is only a
// WebSite + LocalBusiness graph for the corporate office, so it carries no
// listing fields (the old LLM extraction stored "WebSite" as the property
// type and the corporate office as the address from it).
//
// Observed on three sold Sandringham pages (2026-10):
//   <title>104 Abbott Street, Sandringham, VIC 3191 - House for Sale</title>
//   <h1 …>104 Abbott Street, Sandringham</h1>
//   <p>Auction Sale - Sold on Sep 13, 2025</p>
//   <h3 class="h3">Contact Agent for Sold Price</h3>
//   <div aria-label="5 Bed">…<div aria-label="3 Bath">…<div aria-label="3 Car">…
//   <div aria-label="843 m&lt;sup&gt;2&lt;/sup&gt; Area">
//   <a href="/agents/Jack-Liu" …>…<h4 class="text-size-k font-heading">Jack Liu</h4>
//
// The meta description repeats the CAMPAIGN price guide ("$2,300,000 -
// $2,500,000") on sold pages; it is never the sale price. The sold price is
// the h3 under the sold line, which reads "Contact Agent for Sold Price" when
// withheld. Every field is read from the detail block between the <h1> and
// the description heading, because the "sold properties" carousel further
// down repeats the same aria-labels and "Sold Price: $…" for other homes.

import {
  areaValue,
  count,
  decodeEntities,
  formatAddress,
  htmlText,
  parseListingDate,
  propertyTypeLabel,
  readTitle,
  soldAmount,
  stateCode,
} from "../shared/listing-html.ts";

const TITLE_RE =
  /^(.+?),\s*([^,]+?),\s*([A-Z]{2,3})\s+(\d{4})\s*-\s*(.+?)\s+for\s+(?:Sale|Lease|Rent)$/i;
const DETAIL_END_MARKER = '<div class="h-10"></div>';

function detailBlock(html: string): string | null {
  const start = html.search(/<h1[\s>]/i);
  if (start < 0) return null;
  const end = html.indexOf(DETAIL_END_MARKER, start);
  return html.slice(start, end > start ? end : start + 20_000);
}

function ariaCount(block: string, label: string): number | null {
  const match = block.match(new RegExp(`aria-label=["'](\\d+)\\s+${label}["']`, "i"));
  return match ? count(match[1]) : null;
}

export function parseJellisCraigDetailHtml(html: string, _url: string): Record<string, unknown> {
  const title = readTitle(html)?.match(TITLE_RE);
  const block = detailBlock(html);
  // A challenge page or a page without the listing has neither the
  // "<address>, <suburb>, <STATE> <postcode> - <Type> for Sale" title nor the
  // detail block; return no data rather than guess.
  if (!title || !block) return {};

  const street = title[1].trim();
  const suburb = title[2].trim();
  const state = stateCode(title[3]);
  const postcode = title[4];
  const hit: Record<string, unknown> = {
    address: formatAddress(street, suburb, state, postcode),
    suburb,
    state,
    postcode,
    propertyType: propertyTypeLabel(title[5]),
    agencyName: "Jellis Craig",
  };

  const soldLine = block.match(/<p>[^<]*\bSold on\s+([A-Za-z]{3,9}\s+\d{1,2},\s*\d{4})[^<]*<\/p>/i);
  hit.listingType = soldLine ? "sold" : null;
  hit.saleDate = soldLine ? parseListingDate(soldLine[1]) : null;

  // The price heading sits directly under the sold line.
  const priceHeading = block.match(/<h3 class=["']h3["'][^>]*>([\s\S]*?)<\/h3>/i);
  const priceDisplay = priceHeading ? htmlText(priceHeading[1]) || null : null;
  hit.priceDisplay = priceDisplay;
  hit.salePrice = soldLine ? soldAmount(priceDisplay) : null;
  hit.price = hit.salePrice;

  hit.bedrooms = ariaCount(block, "Bed");
  hit.bathrooms = ariaCount(block, "Bath");
  hit.parking = ariaCount(block, "Car");

  // "843 m<sup>2</sup> Area" (entity-encoded in the attribute). Jellis Craig
  // labels it only "Area"; on 104 Abbott Street it matches the copy's "Land
  // size: 843 Square Metres approx.", so it is the land area.
  const area = block.match(
    /aria-label=["']([\d,.]+)\s*m(?:&lt;sup&gt;2&lt;\/sup&gt;|²|2)\s*Area["']/i,
  );
  hit.landSize = area ? areaValue(area[1]) : null;
  hit.landSizeUnit = hit.landSize ? "sqm" : null;

  const agents = new Set<string>();
  for (const match of block.matchAll(
    /href=["']\/agents\/[^"']+["'][\s\S]*?<h4[^>]*>([^<]+)<\/h4>/gi,
  )) {
    const name = decodeEntities(match[1]).trim();
    if (name) agents.add(name);
  }
  hit.agentName = agents.size > 0 ? [...agents].join(", ") : null;

  return hit;
}
